import { InstagramIcon } from 'react-share';
import { useRecoilValue } from 'recoil';
import { userInstagramStateAtom } from '../../social/atom';
import { useState } from 'react';
import AddInstagramDialog from '../../social/AddInstagramDialog';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Instagram } from '@mui/icons-material';
import PublicationTimeChooser from './PublicationTImeChooser';
import useSettings from 'app/hooks/useSettings';
import { toast } from 'react-toastify';
import { getFunctions, httpsCallable } from 'firebase/functions';
import useAuth from 'app/hooks/useAuth';
import { useConfirm } from 'material-ui-confirm';
import { CredentialNameWithImage } from 'app/views/credential';
import { useMemo } from 'react';

const {
  Stack,
  Typography,
  Card,
  Divider,
  Grid,
  Select,
  MenuItem,
  Tab,
  FormControlLabel,
  Switch,
  TextField,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent
} = require('@mui/material');
const { default: Loading } = require('app/components/MatxLoading');

const LanguageOptions = ['French', 'English', 'Spanish', 'German', 'Italian'];
const ToneOptions = [
  'Warm',
  'Welcoming',
  'Enthusiastic',
  'Neutral',
  'Serious',
  'Humorous',
  'Optimistic',
  'Pessimistic',
  'Empathetic',
  'Authoritative',
  'Inspiring',
  'Passionate'
];
const NarrativeStyleOptions = [
  'Descriptive',
  'Persuasive',
  'Narrative',
  'Informative',
  'Creative',
  'Technical'
];

const GeneralTab = ({ instagrams, site, updateSite, handleAddInstagram, callSave }) => {
  const [isEnablingInstagram, setIsEnablingInstagram] = useState(false);
  const [showError, setShowError] = useState(false);
  const { user } = useAuth();

  const handleEnableInstagram = async (e, v) => {
    // console.log(e, v);
    setShowError(false);
    if (!site?.instagram_settings.siteId) {
      toast.error('Please select an Instagram account to enable auto posting.');
      return;
    }
    if (
      !site?.instagram_settings.brand ||
      !site?.instagram_settings.description ||
      !site?.instagram_settings.CTA
    ) {
      setShowError(true);
      toast.error('Please fill in all the branding fields.');
      return;
    }

    setIsEnablingInstagram(true);
    try {
      await updateSite({ instagram_settings: { enabled: e.target.checked } });
      const fn = httpsCallable(getFunctions(), 'instagramRequest');
      await fn({ site, command: e.target.checked ? 'enable' : 'disable' });
      await callSave();
    } catch (err) {
      console.log(err);
      await updateSite({ instagram_settings: { enabled: !e.target.checked } });
      toast.error('Failed to ' + (e.target.checked ? 'enable' : 'disable') + ' Instagram');
    }

    setIsEnablingInstagram(false);
  };

  return (
    <Grid container spacing={2} justifyContent="space-between" padding={'10px'}>
      <Dialog open={isEnablingInstagram}>
        <DialogTitle>
          {site?.instagram_settings.enabled ? 'Enabling Instagram' : 'Disabling Instagram'}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" margin={2} justifyContent={'center'} alignItems={'center'}>
            <Loading />
          </Stack>
        </DialogContent>
      </Dialog>
      <Grid item xs={12} md={6}>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Enable or disable auto posting to Instagram.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              disabled={isEnablingInstagram}
              checked={site?.instagram_settings.enabled}
              onChange={handleEnableInstagram}
            />
          }
          label={site?.instagram_settings.enabled ? 'Enabled' : 'Disabled'}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Assign Instagram accounts to your site to automatically post images to Instagram.
        </Typography>
        <Select
          size="small"
          fullWidth
          value={site?.instagram_settings.siteId}
          onChange={(e) => {
            updateSite({ instagram_settings: { siteId: e.target.value } });
          }}
        >
          {instagrams.map((instagram, index) => (
            <MenuItem key={index} value={instagram.id}>
              {instagram.name}
            </MenuItem>
          ))}

          {user?.plan?.social?.instagram_acc_limit &&
            instagrams.length < user?.plan?.social?.instagram_acc_limit && (
              <MenuItem value={''} onClick={handleAddInstagram}>
                + Add Instagram
              </MenuItem>
            )}
        </Select>
      </Grid>

      <Grid item xs={12} style={{ margin: '5px 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Branding
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Customize the branding of your Instagram posts.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Brand Name"
          size="small"
          fullWidth
          error={showError && !site?.instagram_settings.brand}
          value={site?.instagram_settings.brand}
          onChange={(e) => updateSite({ instagram_settings: { brand: e.target.value } })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Description"
          error={showError && !site?.instagram_settings.description}
          size="small"
          fullWidth
          value={site?.instagram_settings.description}
          onChange={(e) => updateSite({ instagram_settings: { description: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Hashtag"
          size="small"
          fullWidth
          value={site?.instagram_settings.hashtag}
          onChange={(e) => updateSite({ instagram_settings: { hashtag: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Call to Action"
          size="small"
          fullWidth
          error={showError && !site?.instagram_settings.CTA}
          value={site?.instagram_settings.CTA}
          onChange={(e) => updateSite({ instagram_settings: { CTA: e.target.value } })}
        />
      </Grid>
    </Grid>
  );
};

const EditorialGuideTab = ({ site, updateSite, callSave }) => {
  const [triggerNewCreation, setTriggerNewCreation] = useState(false);
  const handleCreatePost = async () => {
    setTriggerNewCreation(true);
    try {
      const fn = httpsCallable(getFunctions(), 'instagramRequest');
      await callSave();
      await fn({ id: site.id, command: 'createPostRightNow' });
      toast.success('post creation task is queued');
    } catch (err) {
      console.log(err);
      toast.error('Failed to create post');
    }
    setTriggerNewCreation(false);
  };
  return (
    <Grid container spacing={3} justifyContent="space-between" padding={'10px'}>
      <Grid item xs={12} style={{ margin: '5px 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Editorial Guide for the AI
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Customize the AI's writing style for Instagram posts.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={site?.instagram_settings.emojies}
              onChange={(e) => updateSite({ instagram_settings: { emojies: e.target.checked } })}
            />
          }
          label="Use Emojies"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={site?.instagram_settings.hashtags}
              onChange={(e) => updateSite({ instagram_settings: { hashtags: e.target.checked } })}
            />
          }
          label=" Use Hashtags"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          freeSolo
          value={site?.instagram_settings.language}
          onChange={(e, value) => updateSite({ instagram_settings: { language: value } })}
          options={LanguageOptions}
          getOptionLabel={(option) => option || ''}
          renderInput={(params) => <TextField {...params} label="Language" size="small" />}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          select
          label="Tone"
          size="small"
          fullWidth
          value={site?.instagram_settings.tone}
          onChange={(e) => updateSite({ instagram_settings: { tone: e.target.value } })}
        >
          {ToneOptions.map((tone, index) => (
            <MenuItem key={index} value={tone}>
              {tone}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Narrative Style"
          fullWidth
          value={site?.instagram_settings.style}
          onChange={(e) => updateSite({ instagram_settings: { style: e.target.value } })}
        >
          {NarrativeStyleOptions.map((style, index) => (
            <MenuItem key={index} value={style}>
              {style}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Address Audience"
          fullWidth
          value={site?.instagram_settings.address}
          onChange={(e) => updateSite({ instagram_settings: { address: e.target.value } })}
        >
          <MenuItem value="Directly">Directly</MenuItem>
          <MenuItem value="Indirectly">Indirectly</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Voice"
          fullWidth
          value={site?.instagram_settings.voice}
          onChange={(e) => updateSite({ instagram_settings: { voice: e.target.value } })}
        >
          <MenuItem value="First Person">First Person</MenuItem>

          <MenuItem value="Third Person">Third Person</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Text Length (Words)"
          size="small"
          fullWidth
          value={site?.instagram_settings.text_length}
          onChange={(e) => updateSite({ instagram_settings: { text_length: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Note to AI"
          placeholder="Write a note to the AI to guide it in creating Instagram posts"
          multiline
          rows={3}
          size="small"
          fullWidth
          value={site?.instagram_settings.note}
          onChange={(e) => updateSite({ instagram_settings: { note: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12}>
        <LoadingButton
          variant="contained"
          onClick={handleCreatePost}
          loading={triggerNewCreation}
          startIcon={<Instagram />}
        >
          Create a post right now
        </LoadingButton>
      </Grid>
    </Grid>
  );
};

function getStyles(value, values) {
  return {
    fontWeight: values.indexOf(value) === -1 ? 400 : 700
  };
}

const VisualTab = ({ site, updateSite, userData }) => {
  const { settings } = useSettings();
  const selectedCredType = useMemo(() => {
    const selected_dc_cred =
      site?.instagram_settings.selected_dc_cred ?? site?.settings.selected_dc_cred;
    if (selected_dc_cred) {
      return userData?.dc_creds[selected_dc_cred]?.type;
    }
  }, [userData, site?.instagram_settings?.selected_dc_cred, site?.settings.selected_dc_cred]);

  return (
    <Grid container spacing={3} justifyContent="space-between" padding={'10px'}>
      <Grid item xs={12} style={{ margin: '5px 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Visual
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Customize the image style of your Posts
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Post Style"
          fullWidth
          value={site?.instagram_settings.post_style}
          onChange={(e) => updateSite({ instagram_settings: { post_style: e.target.value } })}
        >
          <MenuItem value="Image">Image</MenuItem>
          <MenuItem value="Carousel">Carousel Only</MenuItem>
          <MenuItem value="Carousel1in2">Alternate Between Carousel And Image</MenuItem>
          <MenuItem value="Random">Let God Decide</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Image Aspect Ratio"
          fullWidth
          value={site?.instagram_settings.image_ratio}
          onChange={(e) => updateSite({ instagram_settings: { image_ratio: e.target.value } })}
        >
          <MenuItem value="1:1">1:1</MenuItem>
          <MenuItem value="3:2">3:2</MenuItem>
          <MenuItem value="2:3">2:3</MenuItem>
          <MenuItem value="4:5">4:5</MenuItem>
          <MenuItem value="16:9">16:9</MenuItem>
          <MenuItem value="9:16">9:16</MenuItem>
        </TextField>
      </Grid>

      {site?.instagram_settings.post_style !== 'Image' && !selectedCredType && (
        <Grid item xs={12} style={{ maxWidth: '500px' }}>
          <Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={site?.instagram_settings.eco_mode}
                  onChange={(e) =>
                    updateSite({ instagram_settings: { eco_mode: e.target.checked } })
                  }
                />
              }
              label={<Typography variant="h6">Eco Mode</Typography>}
            />
            <Typography maxWidth="600px" variant="body1" color="GrayText" marginBottom="5px">
              If you choose eco-mode for Instagram, we will use 4 images from the same prompt
              through midjourney to create a carousel.
            </Typography>
          </Stack>
        </Grid>
      )}

      <Grid item xs={12} style={{ margin: '5px 0 0 0', maxWidth: '500px' }}>
        <Typography variant="h6">Select Presets</Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Choose from a list of presets to apply to your Posts' images. We will alternate between
          these if multiple presets are selected.
        </Typography>

        <Select
          select
          size="small"
          fullWidth
          multiple
          value={site?.instagram_settings.presets}
          onChange={(e) =>
            updateSite((p) => ({
              ...p,
              instagram_settings: { ...p.instagram_settings, presets: e.target.value }
            }))
          }
        >
          {settings?.presets.map((preset, index) => (
            <MenuItem
              key={preset.id}
              value={preset.id}
              style={getStyles(preset.id, site?.instagram_settings.presets)}
            >
              {preset.name}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12} style={{ margin: '5px 0 0 0', maxWidth: '500px' }}>
        <Typography variant="h6">Select Image Generation Credential</Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Images will be generated using this credential for this site.
        </Typography>

        <Select
          select
          size="small"
          fullWidth
          value={site?.instagram_settings.selected_dc_cred ?? site.settings.selected_dc_cred}
          onChange={(e) => {
            updateSite({
              instagram_settings: { selected_dc_cred: parseInt(e.target.value) }
            });
          }}
        >
          {userData?.dc_creds?.map((key, i) => (
            <MenuItem key={key.name} value={i}>
              <CredentialNameWithImage cred={key} />
            </MenuItem>
          ))}
        </Select>
      </Grid>
    </Grid>
  );
};

const PublicationTimeTab = ({ site, updateSite }) => {
  return (
    <Grid container spacing={3} justifyContent="space-between" padding={'10px'}>
      <Grid item xs={12} style={{ margin: '5px 0 0 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Publication Time
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Control the publication time of your Instagram posts. Changes in schedules will only be in
          effect after the next post is generated.
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Make sure you press <b>"save changes"</b> after making any changes to the publication
          time.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <PublicationTimeChooser site={site} updateSite={updateSite} siteKey="instagram_settings" />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" marginBottom="5px">
          Recycle Posts
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          If no new articles are published on this site, we will use the most recent articles to
          generate Posts, provided that the recycling option is enabled, within the limit of the
          number of reuses defined below. If recycling is disabled, the system will select the last
          5 published articles and look for an article that is still unused to create a post. If no
          available articles are found, no new post will be generated.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={site?.instagram_settings.recycle_post}
              onChange={(e) =>
                updateSite({ instagram_settings: { recycle_post: e.target.checked } })
              }
            />
          }
          label="Recycle Posts"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Number of Recycles"
          size="small"
          fullWidth
          disabled={!site?.instagram_settings.recycle_post}
          value={site?.instagram_settings.number_of_recycles}
          onChange={(e) =>
            updateSite({ instagram_settings: { number_of_recycles: e.target.value } })
          }
        />
      </Grid>
    </Grid>
  );
};

const InstagramSettings = ({ site, updateSite, callSave, userData }) => {
  const instagrams = useRecoilValue(userInstagramStateAtom);
  const [openAddInstagram, setOpenAddInstagram] = useState(false);
  const [selectedTab, setSelectedTab] = useState('general');

  const handleAddInstagram = (e) => {
    e.preventDefault();
    setOpenAddInstagram(true);
  };

  if (!site) {
    return (
      <Stack marginTop="12px">
        <Loading />
      </Stack>
    );
  }

  return (
    <Card>
      <AddInstagramDialog open={openAddInstagram} onClose={() => setOpenAddInstagram(false)} />
      <Typography
        variant="h6"
        sx={{
          color: '#E60023',
          padding: '12px 16px',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <img src="/assets/images/instagram.png" alt="Instagram" style={{ width: '20px' }} />
        Auto Post to Instagram
      </Typography>
      <Divider />

      <TabContext value={selectedTab}>
        <TabList
          onChange={(e, v) => {
            setSelectedTab(v);
          }}
        >
          <Tab label="General" value={'general'} />
          <Tab label="Editorial Guide" value={'editorial'} />
          <Tab label="Publication Time" value={'publication'} />
          <Tab label="Visual" value={'visual'} />
        </TabList>

        <TabPanel value={'general'}>
          <GeneralTab
            instagrams={instagrams}
            site={site}
            updateSite={updateSite}
            handleAddInstagram={handleAddInstagram}
            callSave={callSave}
          />
        </TabPanel>

        <TabPanel value={'editorial'}>
          <EditorialGuideTab site={site} updateSite={updateSite} callSave={callSave} />
        </TabPanel>

        <TabPanel value={'publication'}>
          <PublicationTimeTab site={site} updateSite={updateSite} />
        </TabPanel>

        <TabPanel value={'visual'}>
          <VisualTab site={site} updateSite={updateSite} userData={userData} />
        </TabPanel>
      </TabContext>
    </Card>
  );
};

export default InstagramSettings;
